import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  PatientDocument,
  PatientDocumentIdentifier,
  PatientDocumentScope,
  PatientIdentifier,
  Response,
  UpdatePatientDocumentData,
} from "~/types/api";

/** The list endpoint accepts exactly one scope: `?treatment={id}` or `?unlinked=1`. */
const scopeParams = (scope: PatientDocumentScope) =>
  "unlinked" in scope ? { unlinked: 1 } : { treatment: scope.treatmentId };

export function createPatientDocumentApi({ getRequest, postRequest, patchRequest }: ApiRequestHelpers) {
  const patientDocuments = (patientId: PatientIdentifier, scope: PatientDocumentScope) =>
    getRequest<Response<PatientDocument[]>>(`/patients/${patientId}/documents`, {
      params: scopeParams(scope),
    });

  /** One file per request. The staging area shows the 422 of each row itself, so no toast. */
  const createPatientDocument = (patientId: PatientIdentifier, data: FormData) =>
    postRequest<Response<PatientDocument>>(`/patients/${patientId}/documents`, data, {
      suppressValidationToast: true,
    });

  /** The edit modal shows the 422 on its fields, so no toast. */
  const updatePatientDocument = (
    patientId: PatientIdentifier,
    documentId: PatientDocumentIdentifier,
    data: UpdatePatientDocumentData,
  ) =>
    patchRequest<Response<PatientDocument>>(`/patients/${patientId}/documents/${documentId}`, data, {
      suppressValidationToast: true,
    });

  const downloadPatientDocumentVersion = (
    patientId: PatientIdentifier,
    documentId: PatientDocumentIdentifier,
    versionId: string,
  ) =>
    getRequest<Blob>(`/patients/${patientId}/documents/${documentId}/versions/${versionId}/download`, {
      responseType: "blob",
    });

  return {
    patientDocuments,
    createPatientDocument,
    updatePatientDocument,
    downloadPatientDocumentVersion,
  };
}

export type PatientDocumentApi = ReturnType<typeof createPatientDocumentApi>;
